import React, { useState, useCallback, useRef, useEffect } from 'react';

export interface UseDragAndDropOptions {
  onFilesDropped: (files: File[]) => void;
  disabled?: boolean;
  accept?: string[];
  multiple?: boolean;
}

export const useDragAndDrop = (options: UseDragAndDropOptions) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragDepth, setDragDepth] = useState(0);
  const dropZoneRef = useRef<HTMLElement>(null);

  /**
   * 检查文件是否被接受
   */
  const isFileAccepted = useCallback((file: File): boolean => {
    if (!options.accept || options.accept.length === 0) {
      return true;
    }
    
    return options.accept.some(acceptedType => {
      // 处理MIME类型 (如 'image/*')
      if (acceptedType.includes('*')) {
        const baseType = acceptedType.replace('*', '');
        return file.type.startsWith(baseType);
      }
      // 精确匹配MIME类型 (如 'image/jpeg')
      return file.type === acceptedType;
    });
  }, [options.accept]);

  /**
   * 过滤并验证拖拽的文件
   */
  const filterFiles = useCallback((files: FileList): File[] => {
    const fileArray = Array.from(files);
    
    // 如果不允许多选，只取第一个文件
    const filesToProcess = options.multiple ? fileArray : fileArray.slice(0, 1);
    
    // 过滤接受的文件类型
    return filesToProcess.filter(isFileAccepted);
  }, [options.multiple, isFileAccepted]);

  /**
   * 处理拖拽进入
   */
  const handleDragEnter = useCallback((e: React.DragEvent | DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (options.disabled) return;

    setDragDepth(prev => prev + 1);
    
    if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, [options.disabled]);

  /**
   * 处理拖拽悬停
   */
  const handleDragOver = useCallback((e: React.DragEvent | DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (options.disabled) return;

    // 设置拖拽效果
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
  }, [options.disabled]);

  /**
   * 处理拖拽离开
   */
  const handleDragLeave = useCallback((e: React.DragEvent | DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (options.disabled) return;

    setDragDepth(prev => {
      const newDepth = prev - 1;
      if (newDepth === 0) {
        setIsDragging(false);
      }
      return newDepth;
    });
  }, [options.disabled]);

  /**
   * 处理文件放置
   */
  const handleDrop = useCallback((e: React.DragEvent | DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (options.disabled) return;

    setIsDragging(false);
    setDragDepth(0);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const acceptedFiles = filterFiles(files);
      if (acceptedFiles.length > 0) {
        options.onFilesDropped(acceptedFiles);
      }
    }
  }, [options.disabled, options.onFilesDropped, filterFiles]);

  /**
   * 设置拖拽区域（用于原生DOM事件）
   */
  const setDropZone = useCallback((element: HTMLElement | null) => {
    if (dropZoneRef.current) {
      // 移除旧的事件监听器
      dropZoneRef.current.removeEventListener('dragenter', handleDragEnter as EventListener);
      dropZoneRef.current.removeEventListener('dragover', handleDragOver as EventListener);
      dropZoneRef.current.removeEventListener('dragleave', handleDragLeave as EventListener);
      dropZoneRef.current.removeEventListener('drop', handleDrop as EventListener);
    }

    dropZoneRef.current = element;

    if (element) {
      // 添加新的事件监听器
      element.addEventListener('dragenter', handleDragEnter as EventListener);
      element.addEventListener('dragover', handleDragOver as EventListener);
      element.addEventListener('dragleave', handleDragLeave as EventListener);
      element.addEventListener('drop', handleDrop as EventListener);
    }
  }, [handleDragEnter, handleDragOver, handleDragLeave, handleDrop]);

  /**
   * 获取拖拽区域的属性
   */
  const getDropZoneProps = useCallback(() => ({
    ref: setDropZone,
    'data-dragging': isDragging,
    style: {
      ...(isDragging && {
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        border: '2px dashed #007bff',
        borderRadius: '8px'
      })
    }
  }), [isDragging, setDropZone]);

  /**
   * 获取根容器的属性
   */
  const getRootProps = useCallback(() => ({
    ref: setDropZone,
    'data-dragging': isDragging,
    onDragEnter: handleDragEnter,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop
  }), [isDragging, setDropZone, handleDragEnter, handleDragOver, handleDragLeave, handleDrop]);

  // 清理事件监听器
  useEffect(() => {
    return () => {
      if (dropZoneRef.current) {
        dropZoneRef.current.removeEventListener('dragenter', handleDragEnter as EventListener);
        dropZoneRef.current.removeEventListener('dragover', handleDragOver as EventListener);
        dropZoneRef.current.removeEventListener('dragleave', handleDragLeave as EventListener);
        dropZoneRef.current.removeEventListener('drop', handleDrop as EventListener);
      }
    };
  }, [handleDragEnter, handleDragOver, handleDragLeave, handleDrop]);

  // 阻止浏览器默认拖拽行为
  useEffect(() => {
    const preventDefaults = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    if (!options.disabled) {
      // 阻止浏览器默认的拖拽行为
      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        document.addEventListener(eventName, preventDefaults, false);
      });

      return () => {
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
          document.removeEventListener(eventName, preventDefaults, false);
        });
      };
    }
  }, [options.disabled]);

  return {
    isDragging,
    dragDepth,
    setDropZone,
    getDropZoneProps,
    getRootProps
  };
}; 